import {
    Accordion,
    Avatar,
    DATE_FORMAT,
    IconButton,
    IGuestInfo,
    InfiniteList,
    IReservation,
    Label,
    SectionLabel,
    Spinner,
    StringUtils,
    useConfirmContext,
} from '@localina/core';
import { CloseIcon, EditIcon } from '@localina/icons';
import { useQueryClient } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDeleteGuest, useGuest, useGuestReservationList, useUpdateGuest } from '../../api/queries/guests';
import { useAreas } from '../../api/queries/restaurantAreas';
import { queryKeys } from '../../api/queries/query-keys';
import { GuestReservationListItem, GuestView } from '../../components';
import type { IGuestViewHandle } from '../../components/GuestView/GuestView';
import { GuestStatusIcon } from '../../components/GuestStatusIcon';
import { Path } from '../../enums';
import { GuestUtils, PathUtils } from '../../utils';
import GuestDetails from './GuestDetails';
import { useGuestStats } from './useGuestStats';

interface IPathParams {
    restaurantId: string;
    guestId: string;
}

const Guest: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { restaurantId, guestId } = useParams() as unknown as IPathParams;

    const [guest, setGuest] = React.useState<IGuestInfo | undefined>(undefined);
    const guestViewRef = React.useRef<IGuestViewHandle>(null);

    const { snackbar } = useConfirmContext();
    const queryClient = useQueryClient();
    const guestQuery = useGuest(guestId);
    const guestReservationsQuery = useGuestReservationList(guestId);

    const updateGuestMutation = useUpdateGuest();
    const deleteGuestMutation = useDeleteGuest();

    const stats = useGuestStats(guestQuery.data, guestReservationsQuery);

    const areasQuery = useAreas();
    const areaNames = React.useMemo(
        () => new Map((areasQuery.data?.areas ?? []).map((area) => [area.id, area.name])),
        [areasQuery.data],
    );

    const isLoading = guestQuery.isFetching || updateGuestMutation.isLoading || deleteGuestMutation.isLoading;

    const closePanel = () => {
        navigate(PathUtils.generateUrl(Path.RESTAURANT_GUESTS, { restaurantId }) + location.search);
    };

    React.useEffect(() => {
        setGuest(undefined);
    }, [guestId]);

    const handleEditClick = () => {
        if (guest) {
            guestViewRef.current?.requestClose();
            return;
        }
        if (guestQuery.data) {
            setGuest({
                ...guestQuery.data.guest,
                company: guestQuery.data.guest.company || '',
            });
        }
    };

    const handleCloseEdit = () => {
        setGuest(undefined);
    };

    const handleSave = (value: IGuestInfo) => {
        updateGuestMutation.mutate(value, {
            onSuccess: () => {
                handleCloseEdit();
            },
            onError: () => {
                snackbar({
                    msg: t('guest.details.view.errors.save'),
                    severity: 'error',
                });
            },
        });
    };

    const handleDelete = (value: IGuestInfo) => {
        deleteGuestMutation.mutate(value, {
            onSuccess: () => {
                handleCloseEdit();
                closePanel();
            },
            onError: () => {
                snackbar({
                    msg: t('guest.details.view.errors.delete'),
                    severity: 'error',
                });
            },
        });
    };

    const handleReservation = (item: IReservation) => {
        queryClient.setQueryData(
            queryKeys.restaurants.single.reservations.single(restaurantId, item.id),
            (oldData: undefined | IReservation) => (oldData ? undefined : item),
        );
        navigate(
            PathUtils.generateUrl(Path.RESTAURANT_GUEST_RESERVATION, {
                restaurantId,
                guestId,
                reservationId: item.id,
            }),
        );
    };

    const details = guestQuery.data;
    const guestName = GuestUtils.formatGuestName(details?.guest);
    const guestSince =
        details?.firstReservationDateTime &&
        DateTime.fromISO(details.firstReservationDateTime, { zone: 'utc' }).toLocal().toFormat(DATE_FORMAT);

    const renderStat = (label: string, value?: React.ReactNode) => (
        <div className="guest-panel__stat">
            <Label type="label" value={label} extraClassName="guest-panel__stat__label" />
            <Label
                type="title"
                variant="h6"
                value={value ?? t('guest.panel.notYetKnown')}
                extraClassName="guest-panel__stat__value"
            />
        </div>
    );

    return (
        <aside className="guest-panel" aria-label={guestName}>
            <div className="guest-panel__header">
                <div className="guest-panel__header__text">
                    <div className="guest-panel__header__title">
                        {details?.guest && <GuestStatusIcon guestStatus={details.guest.guestStatus} />}
                        <Label type="title" variant="h5" value={guestName} />
                    </div>
                    {Boolean(guestSince) && (
                        <Label
                            type="info"
                            value={t('guest.panel.guestSince', { date: guestSince })}
                            extraClassName="guest-panel__header__sub"
                        />
                    )}
                </div>
                <IconButton icon={<CloseIcon />} className="guest-panel__close" onClick={closePanel} />
            </div>

            <div className="guest-panel__body" id="guest">
                {details?.guest && (
                    <>
                        <div className="guest-panel__identity">
                            <Avatar name={guestName} size="large" />
                            <div className="guest-panel__identity__text">
                                <Label type="title" variant="h6" value={guestName} />
                                <Label
                                    type="info"
                                    value={t('guest.panel.summary', {
                                        count: details.numReservations,
                                        noShows: stats?.noShows ?? t('guest.panel.notYetKnown'),
                                    })}
                                />
                            </div>
                            {}
                            <IconButton
                                icon={<EditIcon />}
                                className={StringUtils.combineStrings(['guest-panel__edit', guest && 'is-active'])}
                                onClick={handleEditClick}
                            />
                        </div>

                        <div className="guest-panel__stats">
                            {renderStat(t('guest.panel.visits'), details.numReservations)}
                            {renderStat(
                                t('guest.panel.avgParty'),

                                stats?.averageParty !== undefined ? stats.averageParty.toFixed(1) : undefined,
                            )}
                        </div>

                        {}
                        <Accordion title={t('guest.details.title')} defaultExpanded collapsible={!guest}>
                            {guest ? (
                                <GuestView
                                    ref={guestViewRef}
                                    guest={guest}
                                    onClose={handleCloseEdit}
                                    onSave={handleSave}
                                    onDelete={handleDelete}
                                />
                            ) : (
                                <GuestDetails details={details} />
                            )}
                        </Accordion>

                        {!guest && (
                            <>
                                {}
                                <SectionLabel
                                    value={t('guest.reservations.title')}
                                    extraClassName="guest-panel__section"
                                />
                                <InfiniteList
                                    scrollableTarget="guest"
                                    hasMore={Boolean(guestReservationsQuery.hasNextPage)}
                                    items={(guestReservationsQuery.data?.pages || []).flatMap((page) =>
                                        page.items.map((item) => (
                                            <GuestReservationListItem
                                                key={item.id}
                                                reservation={item}
                                                areaName={item.areaIds
                                                    .map((id) => areaNames.get(id))
                                                    .filter(Boolean)
                                                    .join(', ')}
                                                onClick={handleReservation}
                                            />
                                        )),
                                    )}
                                    loadNext={guestReservationsQuery.fetchNextPage}
                                />
                            </>
                        )}
                    </>
                )}
            </div>
            {}
            {isLoading && (
                <div className="guest-panel__loading">
                    <Spinner position="relative" />
                </div>
            )}
            <Outlet />
        </aside>
    );
};

export default Guest;
